import React, { useState, useEffect } from 'react';
import {
    ArrowLeft,
    Settings,
    MessageSquare,
    CheckCircle,
    AlertCircle,
    Loader2,
    FileText,
    ToggleLeft,
    ToggleRight,
    Eye,
    EyeOff,
    RefreshCw,
    Plus,
    Edit3,
    Save,
    X,
    KeyRound,
    ShieldCheck,
    Send,
    Globe,
    User,
    Lock,
    Info,
    Server,
    Activity,
    Database
} from 'lucide-react';
import { apiFetch } from '../api';
import AnimatedBackground from './Background';
import { ensureSocketConnection } from '../websocket/socketUtils';
import { useWebSocket } from '../websocket/WebSocketContext';

// Modal component for license, confirmation, and config details
const Modal = ({ isOpen, onClose, children, title }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-slate-900/95 border border-purple-500/40 rounded-2xl shadow-2xl p-8 max-w-lg w-full relative transform transition-all duration-300 scale-95 animate-in fade-in-0 zoom-in-95">
                <button onClick={onClose} className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors">
                    <X className="w-6 h-6" />
                </button>
                {title && (
                    <h3 className="text-2xl font-bold mb-6 text-center text-white">{title}</h3>
                )}
                {children}
            </div>
        </div>
    );
};

export default function SMSConfig({ onBack, setError, API_BASE_URL, loginId, deliveryMode }) {
    const { client, connected } = useWebSocket();
    const [configs, setConfigs] = useState([]);
    const [selectedConfig, setSelectedConfig] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setErrorLocal] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

    // Form state for new/edit SMS configuration
    const [formData, setFormData] = useState({
        sms_config_app_name: '',
        sms_url: '',
        sms_username: '',
        sms_password: '',
        sms_sender: '',
        sms_vendor_name: '',
        sms_config_type: '',
        notification_email: ''
    });

    // State for modals and update flow
    const [isLicenseModalOpen, setIsLicenseModalOpen] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [configToUpdate, setConfigToUpdate] = useState(null);
    const [licenseNo, setLicenseNo] = useState('');
    const [licenseError, setLicenseError] = useState('');

    // Queue management state
    const [selectedQueueType, setSelectedQueueType] = useState('0');
    const [currentQueueType, setCurrentQueueType] = useState(null);
    const [isLoadingQueue, setIsLoadingQueue] = useState(false);
    const [isUpdatingQueue, setIsUpdatingQueue] = useState(false);
    const [queueSuccess, setQueueSuccess] = useState('');
    const [queueError, setQueueError] = useState('');

    // App Name status management state
    const [appNameStatuses, setAppNameStatuses] = useState({});
    const [isLoadingAppStatuses, setIsLoadingAppStatuses] = useState(false);
    const [isUpdatingAppStatus, setIsUpdatingAppStatus] = useState(false);
    const [updatingAppName, setUpdatingAppName] = useState(null);

    // Queue type options
    const queueTypes = [
        { value: '0', label: 'Local Queue', icon: <Server className="w-4 h-4" />, color: 'blue' },
        { value: '1', label: 'Kafka Queue', icon: <Activity className="w-4 h-4" />, color: 'green' },
        { value: '2', label: 'Redis Queue', icon: <Database className="w-4 h-4" />, color: 'red' }
    ];


    // Clear messages after timeout
    useEffect(() => {
        if (success || error) {
            const timer = setTimeout(() => {
                setSuccess('');
                setErrorLocal('');
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [success, error]);

    useEffect(() => {
        if (queueSuccess || queueError) {
            const timer = setTimeout(() => {
                setQueueSuccess('');
                setQueueError('');
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [queueSuccess, queueError]);


    // --- 1. Fetch App Name Statuses ---
    const fetchAppNameStatuses = async () => {
        setIsLoadingAppStatuses(true);
        try {
            if (deliveryMode === 'socket') {
                // --- WEBSOCKET LOGIC ---
                try {
                    await ensureSocketConnection(client);
                } catch (connError) {
                    console.error(connError);
                    setQueueError('Socket connection failed.');
                    setIsLoadingAppStatuses(false);
                    return;
                }

                const topic = `/topic/get/all/sms/appName/status/${loginId}`;
                const subscription = client.subscribe(topic, (msg) => {
                    try {
                        const payload = JSON.parse(msg.body);
                        const data = (typeof payload === 'string') ? JSON.parse(payload) : payload;

                        if (data.STATUS === 'SUCCESS' && data.DATA) {
                            setAppNameStatuses(data.DATA);
                        }
                    } catch (e) {
                        console.error('Failed to parse app statuses socket response', e);
                    } finally {
                        subscription.unsubscribe();
                        setIsLoadingAppStatuses(false);
                    }
                });

                client.publish({
                    destination: '/app/get/all/sms/appName/status',
                    body: JSON.stringify({})
                });

            } else {
                // --- API LOGIC ---
                const response = await apiFetch('/get/all/sms/appName/status', {
                    method: 'GET'
                });

                if (response.ok) {
                    const data = await response.json();
                    if (data.STATUS === 'SUCCESS' && data.DATA) {
                        setAppNameStatuses(data.DATA);
                    }
                }
                setIsLoadingAppStatuses(false);
            }
        } catch (err) {
            console.error("Error fetching app name statuses:", err);
            setQueueError('Failed to load app name statuses. Network error.');
            setIsLoadingAppStatuses(false);
        }
    };

    // --- 2. Toggle App Name Status ---
    const toggleAppNameStatus = async (appName, currentStatus) => {
        setIsUpdatingAppStatus(true);
        setUpdatingAppName(appName);
        setQueueSuccess('');
        setQueueError('');

        const newStatus = currentStatus ? 'false' : 'true';

        try {
            if (deliveryMode === 'socket') {
                // --- WEBSOCKET LOGIC ---
                try {
                    await ensureSocketConnection(client);
                } catch (connError) {
                    console.error(connError);
                    setQueueError('Socket connection failed.');
                    setIsUpdatingAppStatus(false);
                    setUpdatingAppName(null);
                    return;
                }

                const topic = `/topic/active-or-de-active/sms/appName/queue/${loginId}`;
                const subscription = client.subscribe(topic, (msg) => {
                    try {
                        const payload = JSON.parse(msg.body);
                        const data = (typeof payload === 'string') ? JSON.parse(payload) : payload;

                        if (data.STATUS === 'SUCCESS') {
                            const statusText = newStatus === 'true' ? 'activated' : 'deactivated';
                            setQueueSuccess(`App "${appName}" ${statusText} successfully!`);
                            fetchAppNameStatuses();
                        } else {
                            setQueueError(`Failed to update app status. ${data.MESSAGE || 'Unknown error occurred.'}`);
                        }
                    } catch (e) {
                        console.error('Failed to parse toggle app status socket response', e);
                        setQueueError('Failed to parse server response.');
                    } finally {
                        subscription.unsubscribe();
                        setIsUpdatingAppStatus(false);
                        setUpdatingAppName(null);
                    }
                });

                client.publish({
                    destination: '/app/active-or-de-active/sms/appName/queue',
                    body: JSON.stringify({
                        appName: appName,
                        active: newStatus
                    })
                });

            } else {
                // --- API LOGIC ---
                const response = await apiFetch(`/active-or-de-active/sms/appName/queue?appName=${appName}&active=${newStatus}`, {
                    method: 'PUT'
                });

                const data = await response.json();

                if (response.ok && data.STATUS === 'SUCCESS') {
                    const statusText = newStatus === 'true' ? 'activated' : 'deactivated';
                    setQueueSuccess(`App "${appName}" ${statusText} successfully!`);
                    await fetchAppNameStatuses();
                } else {
                    setQueueError(`Failed to update app status. ${data.MESSAGE || 'Unknown error occurred.'}`);
                }
                setIsUpdatingAppStatus(false);
                setUpdatingAppName(null);
            }
        } catch (err) {
            console.error("Error updating app name status:", err);
            setQueueError('Failed to update app status. Network error.');
            setIsUpdatingAppStatus(false);
            setUpdatingAppName(null);
        }
    };

    // --- 3. Fetch Current Queue Type ---
    const fetchCurrentQueueType = async () => {
        setIsLoadingQueue(true);
        try {
            if (deliveryMode === 'socket') {
                // --- WEBSOCKET LOGIC ---
                try {
                    await ensureSocketConnection(client);
                } catch (connError) {
                    console.error(connError);
                    setIsLoadingQueue(false);
                    return;
                }

                const topic = `/topic/running/queue/${loginId}`;
                const subscription = client.subscribe(topic, (msg) => {
                    try {
                        const payload = JSON.parse(msg.body);
                        const data = (typeof payload === 'string') ? JSON.parse(payload) : payload;

                        if (data.STATUS === 'SUCCESS') {
                            setCurrentQueueType(data.data.toString());
                        }
                    } catch (e) {
                        console.error('Failed to parse queue type socket response', e);
                    } finally {
                        subscription.unsubscribe();
                        setIsLoadingQueue(false);
                    }
                });

                // Matches API param: type=2 (SMS)
                client.publish({
                    destination: '/app/running/queue',
                    body: JSON.stringify({ type: '2' })
                });

            } else {
                // --- API LOGIC ---
                const response = await apiFetch('/running/queue?type=2', {
                    method: 'GET'
                });

                if (response.ok) {
                    const data = await response.json();
                    if (data.STATUS === 'SUCCESS') {
                        setCurrentQueueType(data.data.toString());
                    }
                }
                setIsLoadingQueue(false);
            }
        } catch (err) {
            console.error("Error fetching current queue type:", err);
            setIsLoadingQueue(false);
        }
    };

    // --- 4. Update Queue Type ---
    const updateQueueType = async () => {
        setIsUpdatingQueue(true);
        setQueueSuccess('');
        setQueueError('');

        try {
            if (deliveryMode === 'socket') {
                // --- WEBSOCKET LOGIC ---
                try {
                    await ensureSocketConnection(client);
                } catch (connError) {
                    console.error(connError);
                    setQueueError('Socket connection failed.');
                    setIsUpdatingQueue(false);
                    return;
                }

                const topic = `/topic/active-or-de-active/sms/queue/${loginId}`;
                const subscription = client.subscribe(topic, (msg) => {
                    try {
                        const payload = JSON.parse(msg.body);
                        const data = (typeof payload === 'string') ? JSON.parse(payload) : payload;

                        if (data.STATUS === 'SUCCESS') {
                            const queueLabel = queueTypes.find(q => q.value === selectedQueueType)?.label;
                            setQueueSuccess(`Queue type switched to ${queueLabel} successfully!`);
                            setCurrentQueueType(selectedQueueType);
                        } else {
                            setQueueError(`Failed to update queue type. ${data.MESSAGE || 'Unknown error occurred.'}`);
                        }
                    } catch (e) {
                        console.error('Failed to parse update queue type socket response', e);
                        setQueueError('Failed to parse server response.');
                    } finally {
                        subscription.unsubscribe();
                        setIsUpdatingQueue(false);
                    }
                });

                client.publish({
                    destination: '/app/active-or-de-active/sms/queue',
                    body: JSON.stringify({
                        type: selectedQueueType
                    })
                });

            } else {
                // --- API LOGIC ---
                const response = await apiFetch(`/active-or-de-active/sms/queue?type=${selectedQueueType}`, {
                    method: 'POST'
                });

                const data = await response.json();

                if (response.ok && data.STATUS === 'SUCCESS') {
                    const queueLabel = queueTypes.find(q => q.value === selectedQueueType)?.label;
                    setQueueSuccess(`Queue type switched to ${queueLabel} successfully!`);
                    setCurrentQueueType(selectedQueueType);
                } else {
                    setQueueError(`Failed to update queue type. ${data.MESSAGE || 'Unknown error occurred.'}`);
                }
                setIsUpdatingQueue(false);
            }
        } catch (err) {
            console.error("Error updating queue type:", err);
            setQueueError('Failed to update queue type. Network error.');
            setIsUpdatingQueue(false);
        }
    };

    // --- 5. Fetch Configurations ---
    const fetchConfigs = async () => {
        setIsLoading(true);
        try {
            if (deliveryMode === 'socket') {
                // --- WEBSOCKET LOGIC ---
                try {
                    await ensureSocketConnection(client);
                } catch (connError) {
                    console.error(connError);
                    setErrorLocal('Socket connection failed.');
                    setIsLoading(false);
                    return;
                }

                const topic = `/topic/sms/config/getAll/${loginId}`;
                const subscription = client.subscribe(topic, (msg) => {
                    try {
                        const payload = JSON.parse(msg.body);
                        const data = (typeof payload === 'string') ? JSON.parse(payload) : payload;

                        if (data.STATUS === 'SUCCESS' && Array.isArray(data.Response)) {
                            setConfigs(data.Response);

                            if (data.Response.length > 0 && !selectedConfig) {
                                setSelectedConfig(data.Response[0]);
                            } else if (selectedConfig) {
                                const updatedSelected = data.Response.find(c => c._id === selectedConfig._id);
                                if (updatedSelected) {
                                    setSelectedConfig(updatedSelected);
                                }
                            }
                        } else {
                            setErrorLocal('Failed to load SMS configurations via socket.');
                        }
                    } catch (e) {
                        console.error('Failed to parse configs socket response', e);
                        setErrorLocal('Failed to parse configuration data.');
                    } finally {
                        subscription.unsubscribe();
                        setIsLoading(false);
                    }
                });

                client.publish({
                    destination: '/app/sms/config/getAll',
                    body: JSON.stringify({})
                });

            } else {
                // --- API LOGIC ---
                const response = await apiFetch(`/sms/config/getAll`);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();

                if (data.STATUS === 'SUCCESS' && Array.isArray(data.Response)) {
                    setConfigs(data.Response);

                    if (data.Response.length > 0 && !selectedConfig) {
                        setSelectedConfig(data.Response[0]);
                    } else if (selectedConfig) {
                        const updatedSelected = data.Response.find(c => c._id === selectedConfig._id);
                        if (updatedSelected) {
                            setSelectedConfig(updatedSelected);
                        }
                    }
                } else {
                    setErrorLocal('Failed to load SMS configurations.');
                }
                setIsLoading(false);
            }
        } catch (err) {
            console.error("Error fetching SMS configs:", err);
            setErrorLocal('Failed to load SMS configurations. Network error.');
            setIsLoading(false);
        }
    };

    // --- 6. Execute Status Update (Active/Deactive) ---
    const executeStatusUpdate = async () => {
        if (!configToUpdate || !licenseNo) return;

        setIsConfirmModalOpen(false);
        setIsUpdating(true);
        const newActive = configToUpdate.STATUS !== 'ACTIVE'; // Corrected boolean logic

        try {
            if (deliveryMode === 'socket') {
                // --- WEBSOCKET LOGIC ---
                try {
                    await ensureSocketConnection(client);
                } catch (connError) {
                    console.error(connError);
                    setErrorLocal('Socket connection failed.');
                    setIsUpdating(false);
                    return;
                }

                const topic = `/topic/sms/config/active/deActive/${loginId}`;
                const subscription = client.subscribe(topic, (msg) => {
                    try {
                        const payload = JSON.parse(msg.body);
                        const data = (typeof payload === 'string') ? JSON.parse(payload) : payload;

                        if (data.STATUS === 'SUCCESS') {
                            const newStatus = data.status;
                            setSuccess(`SMS Configuration ${newStatus.toLowerCase()}d successfully!`);

                            fetchCurrentQueueType();
                            fetchAppNameStatuses();

                            const updatedConfigs = configs.map(c =>
                                c._id === configToUpdate._id ? { ...c, STATUS: newStatus } : c
                            );
                            setConfigs(updatedConfigs);

                            if (selectedConfig && selectedConfig._id === configToUpdate._id) {
                                setSelectedConfig(prev => ({ ...prev, STATUS: newStatus }));
                            }
                        } else {
                            setErrorLocal(`Failed to update: ${data.message || 'Unknown error'}`);
                        }
                    } catch (e) {
                        console.error('Failed to parse status update socket response', e);
                        setErrorLocal('Failed to parse server response.');
                    } finally {
                        subscription.unsubscribe();
                        setIsUpdating(false);
                        setConfigToUpdate(null);
                        setLicenseNo('');
                    }
                });

                client.publish({
                    destination: '/app/sms/config/active/deActive',
                    body: JSON.stringify({
                        id: configToUpdate._id,
                        active: newActive,
                        licenseNumber: licenseNo
                    })
                });

            } else {
                // --- API LOGIC ---
                const response = await apiFetch(
                    `/sms/config/active/deActive?id=${configToUpdate._id}&active=${newActive}&licenseNumber=${licenseNo}`,
                    {
                        method: 'PUT'
                    }
                );

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();

                if (data.STATUS === 'SUCCESS') {
                    const newStatus = data.status;
                    setSuccess(`SMS Configuration ${newStatus.toLowerCase()}d successfully!`);

                    await fetchCurrentQueueType();
                    await fetchAppNameStatuses();

                    const updatedConfigs = configs.map(c =>
                        c._id === configToUpdate._id ? { ...c, STATUS: newStatus } : c
                    );
                    setConfigs(updatedConfigs);

                    if (selectedConfig && selectedConfig._id === configToUpdate._id) {
                        setSelectedConfig(prev => ({ ...prev, STATUS: newStatus }));
                    }

                } else {
                    setErrorLocal(`Failed to update: ${data.message || 'Unknown error'}`);
                }
                setIsUpdating(false);
                setConfigToUpdate(null);
                setLicenseNo('');
            }
        } catch (err) {
            console.error("Error toggling SMS config status:", err);
            setErrorLocal('Failed to update SMS configuration status. Network error.');
            setIsUpdating(false);
            setConfigToUpdate(null);
            setLicenseNo('');
        }
    };

    // --- 7. Save SMS Configuration ---
    const handleSaveConfig = async () => {
        setIsSaving(true);
        setSuccess('');
        setErrorLocal('');

        const requiredFields = [
            'sms_config_app_name', 'sms_url', 'sms_username',
            'sms_password', 'sms_sender', 'sms_vendor_name', 'sms_config_type', 'notification_email'
        ];

        for (const field of requiredFields) {
            if (!formData[field]?.trim()) {
                setErrorLocal(`${field.replace(/_/g, ' ').toUpperCase()} is required.`);
                setIsSaving(false);
                return;
            }
        }

        try {
            if (deliveryMode === 'socket') {
                // --- WEBSOCKET LOGIC ---
                try {
                    await ensureSocketConnection(client);
                } catch (connError) {
                    console.error(connError);
                    setErrorLocal('Socket connection failed.');
                    setIsSaving(false);
                    return;
                }

                const isUpdate = isEditing && selectedConfig;
                const endpoint = isUpdate ? 'update' : 'save';
                const topic = `/topic/sms/config/${endpoint}/${loginId}`;
                const destination = `/app/sms/config/${endpoint}`;

                const subscription = client.subscribe(topic, (msg) => {
                    try {
                        const payload = JSON.parse(msg.body);
                        const data = (typeof payload === 'string') ? JSON.parse(payload) : payload;

                        if (data.STATUS === 'SUCCESS') {
                            setSuccess(`SMS Configuration ${isEditing ? 'updated' : 'saved'} successfully!`);
                            resetForm();
                            fetchConfigs();
                            fetchCurrentQueueType();
                            fetchAppNameStatuses();
                        } else {
                            setErrorLocal(`Failed to ${isEditing ? 'update' : 'save'} SMS configuration: ${data.message || 'Unknown error'}`);
                        }
                    } catch (e) {
                        console.error('Failed to parse save config socket response', e);
                        setErrorLocal('Failed to parse server response.');
                    } finally {
                        subscription.unsubscribe();
                        setIsSaving(false);
                    }
                });

                // Prepare Payload
                const payload = { ...formData };
                if (isUpdate) payload.id = selectedConfig._id;

                client.publish({
                    destination: destination,
                    body: JSON.stringify(payload)
                });

            } else {
                // --- API LOGIC ---
                let response;
                if (isEditing && selectedConfig) {
                    response = await apiFetch(`/sms/config/update?id=${selectedConfig._id}`, {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(formData),
                    });
                } else {
                    response = await apiFetch('/sms/config/save', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(formData),
                    });
                }

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();

                if (data.STATUS === 'SUCCESS') {
                    setSuccess(`SMS Configuration ${isEditing ? 'updated' : 'saved'} successfully!`);
                    resetForm();
                    await fetchConfigs();
                    await fetchCurrentQueueType();
                    await fetchAppNameStatuses();
                } else {
                    setErrorLocal(`Failed to ${isEditing ? 'update' : 'save'} SMS configuration: ${data.message || 'Unknown error'}`);
                }
                setIsSaving(false);
            }
        } catch (err) {
            console.error("Error saving SMS config:", err);
            setErrorLocal(`Failed to ${isEditing ? 'update' : 'save'} SMS configuration. Network error.`);
            setIsSaving(false);
        }
    };

    // Handle toggle button click
    const handleToggleClick = (config) => {
        setConfigToUpdate(config);
        setIsLicenseModalOpen(true);
    };

    // Handle license submission
    const handleLicenseSubmit = () => {
        if (!licenseNo.trim()) {
            setLicenseError('License number cannot be empty.');
            return;
        }
        setLicenseError('');
        setIsLicenseModalOpen(false);
        setIsConfirmModalOpen(true);
    };

    // Handle form input changes
    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            sms_config_app_name: '',
            sms_url: '',
            sms_username: '',
            sms_password: '',
            sms_sender: '',
            sms_vendor_name: '',
            sms_config_type: ''
        });
        setIsEditing(false);
    };

    // Load config into form for editing
    const loadConfigForEdit = (config) => {
        setFormData({
            sms_config_app_name: config.sms_config_app_name || '',
            sms_url: config.sms_url || '',
            sms_username: config.sms_username || '',
            sms_password: config.sms_password || '',
            sms_sender: config.sms_sender || '',
            sms_vendor_name: config.sms_vendor_name || '',
            sms_config_type: config.sms_config_type || '',
            token: config.token || '',
            peId: config.peId || '',
            dltChain: config.dltChain || ''
        });
        setIsEditing(true);
    };

    const handleRefresh = async () => {
        fetchConfigs();
        fetchCurrentQueueType();
        fetchAppNameStatuses();
    }

    // Initial load
    useEffect(() => {
        fetchConfigs();
        fetchCurrentQueueType();
        fetchAppNameStatuses();
    }, [API_BASE_URL]);

    // Format timestamp
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';
        try {
            const date = new Date(timestamp);
            return date.toLocaleString();
        } catch {
            return timestamp;
        }
    };

    const getCurrentQueueLabel = () => {
        return queueTypes.find(q => q.value === currentQueueType)?.label || 'Unknown';
    };

    const getCurrentQueueColor = () => {
        return queueTypes.find(q => q.value === currentQueueType)?.color || 'gray';
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 to-[#649EE8] font-inter text-white">

            {/* Render the animated background */}
            <AnimatedBackground />

            {/* Background Elements */}
            {/* <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-1/3 -left-1/3 w-2/3 h-2/3 bg-gradient-to-br from-blue-600/10 to-transparent rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute -bottom-1/3 -right-1/3 w-2/3 h-2/3 bg-gradient-to-tl from-purple-600/10 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1.5s' }}></div>
            </div> */}

            <div className="relative z-10 p-6 md:p-10 max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-10">
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={onBack}
                            className="flex items-center space-x-2 px-5 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-all duration-300 shadow-md hover:shadow-lg"
                        >
                            <ArrowLeft className="w-5 h-5" />
                            <span>Back to Dashboard</span>
                        </button>
                        <div className="flex items-center space-x-4">
                            <div className="w-14 h-14 bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
                                <MessageSquare className="w-7 h-7" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold tracking-tight">SMS Configuration</h1>
                                <p className="text-white/70 text-sm">Manage your SMS service settings</p>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center space-x-3">
                        <button
                            onClick={fetchCurrentQueueType}
                            disabled={isLoadingQueue}
                            className="flex items-center space-x-2 px-4 py-2 bg-orange-500/20 hover:bg-orange-500/30 border border-orange-500/40 rounded-xl text-orange-200 transition-all duration-300 disabled:opacity-50"
                        >
                            <RefreshCw className={`w-4 h-4 ${isLoadingQueue ? 'animate-spin' : ''}`} />
                            <span>Queue Status</span>
                        </button>
                        <button
                            onClick={fetchAppNameStatuses}
                            disabled={isLoadingAppStatuses}
                            className="flex items-center space-x-2 px-4 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/40 rounded-xl text-cyan-200 transition-all duration-300 disabled:opacity-50"
                        >
                            <RefreshCw className={`w-4 h-4 ${isLoadingAppStatuses ? 'animate-spin' : ''}`} />
                            <span>App Status</span>
                        </button>
                        <button
                            onClick={handleRefresh}
                            disabled={isLoading}
                            className="flex items-center space-x-2 px-5 py-3 bg-purple-600/20 hover:bg-purple-600/30 border border-purple-600/40 rounded-xl text-purple-200 transition-all duration-300 disabled:opacity-50 shadow-md hover:shadow-lg"
                        >
                            <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
                            <span>Refresh</span>
                        </button>
                    </div>
                </div>

                {/* Global Messages */}
                {(success || error || queueSuccess || queueError) && (
                    <div className="mb-6 space-y-3">
                        {(success || error) && (
                            <div className={`p-4 rounded-xl flex items-center space-x-3 border backdrop-blur-sm ${success
                                ? 'bg-green-500/20 border-green-500/40 text-green-200'
                                : 'bg-red-500/20 border-red-500/40 text-red-200'
                                }`}>
                                {success ? <CheckCircle className="w-5 h-5 flex-shrink-0" /> : <AlertCircle className="w-5 h-5 flex-shrink-0" />}
                                <span className="font-medium">{success || error}</span>
                            </div>
                        )}
                        {(queueSuccess || queueError) && (
                            <div className={`p-4 rounded-xl flex items-center space-x-3 border backdrop-blur-sm ${queueSuccess
                                ? 'bg-green-500/20 border-green-500/40 text-green-200'
                                : 'bg-red-500/20 border-red-500/40 text-red-200'
                                }`}>
                                {queueSuccess ? <CheckCircle className="w-5 h-5 flex-shrink-0" /> : <AlertCircle className="w-5 h-5 flex-shrink-0" />}
                                <span className="font-medium">{queueSuccess || queueError}</span>
                            </div>
                        )}
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Left Sidebar - SMS Config List */}
                    <div className="lg:col-span-1">
                        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 shadow-xl">
                            <div className="flex items-center space-x-3 mb-6">
                                <MessageSquare className="w-6 h-6 text-green-400" />
                                <h3 className="text-xl font-semibold">SMS Configurations</h3>
                                <span className="px-3 py-1 bg-green-600/20 text-green-300 text-sm rounded-full">
                                    {configs.length}
                                </span>
                            </div>

                            {isLoading ? (
                                <div className="flex items-center justify-center py-10">
                                    <Loader2 className="w-7 h-7 animate-spin text-green-400" />
                                    <span className="ml-3 text-white/70">Loading...</span>
                                </div>
                            ) : configs.length > 0 ? (
                                <div className="space-y-4 max-h-[600px] overflow-y-auto custom-scrollbar pr-2">
                                    {configs.map((config) => {
                                        // --- Determine SMS App Status ---
                                        const appNameStatus = appNameStatuses[config.sms_config_app_name];
                                        const isAppActive = appNameStatus === true || appNameStatus === "true";
                                        const isCurrentlyUpdating =
                                            updatingAppName === config._id && isUpdatingAppStatus;

                                        return (
                                            <div
                                                key={config._id}
                                                onClick={() => {
                                                    setSelectedConfig(config);
                                                    setIsDetailsModalOpen(true);
                                                }}
                                                className={`p-5 rounded-xl border cursor-pointer transition-all duration-300 hover:scale-[1.02] shadow-sm hover:shadow-md ${selectedConfig?._id === config._id
                                                    ? "bg-green-600/20 border-green-500/50"
                                                    : "bg-white/5 border-white/10 hover:bg-white/10"
                                                    }`}
                                            >
                                                <div className="flex items-center justify-between mb-2">
                                                    <h4 className="font-semibold text-base">{config.sms_config_app_name}</h4>
                                                    <div
                                                        className={`flex items-center space-x-1 px-3 py-1 rounded-full text-sm ${config.STATUS === "ACTIVE"
                                                            ? "bg-green-600/20 text-green-300"
                                                            : "bg-red-600/20 text-red-300"
                                                            }`}
                                                    >
                                                        <div
                                                            className={`w-2.5 h-2.5 rounded-full ${config.STATUS === "ACTIVE"
                                                                ? "bg-green-400"
                                                                : "bg-red-400"
                                                                }`}
                                                        ></div>
                                                        <span>{config.STATUS}</span>
                                                    </div>
                                                </div>
                                                <p className="text-sm text-white/60 mb-3">
                                                    Vendor: {config.sms_vendor_name}
                                                </p>

                                                {/* --- Copied & Integrated App Name Status Toggle --- */}
                                                <div className="flex items-center justify-between bg-white/5 rounded-lg p-2 mb-3">
                                                    <div className="flex items-center space-x-2">
                                                        <span className="text-xs text-white/60">App Status:</span>
                                                        <div
                                                            className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs ${isAppActive
                                                                ? "bg-green-500/20 text-green-300"
                                                                : appNameStatus === false ||
                                                                    appNameStatus === "false"
                                                                    ? "bg-red-500/20 text-red-300"
                                                                    : "bg-gray-500/20 text-gray-300"
                                                                }`}
                                                        >
                                                            <div
                                                                className={`w-2 h-2 rounded-full ${isAppActive
                                                                    ? "bg-green-400"
                                                                    : appNameStatus === false ||
                                                                        appNameStatus === "false"
                                                                        ? "bg-red-400"
                                                                        : "bg-gray-400"
                                                                    }`}
                                                            ></div>
                                                            <span>
                                                                {isLoadingAppStatuses
                                                                    ? "Loading..."
                                                                    : isAppActive
                                                                        ? "Active"
                                                                        : appNameStatus === false ||
                                                                            appNameStatus === "false"
                                                                            ? "Inactive"
                                                                            : "Unknown"}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation(); // Prevent modal from opening
                                                            toggleAppNameStatus(config.sms_config_app_name, isAppActive);
                                                        }}
                                                        disabled={
                                                            isCurrentlyUpdating ||
                                                            isLoadingAppStatuses ||
                                                            appNameStatus === undefined
                                                        }
                                                        className={`flex items-center space-x-1 px-2 py-1 rounded-lg transition-all duration-300 text-xs ${isAppActive
                                                            ? "bg-red-500/20 border border-red-500/40 text-red-200 hover:bg-red-500/30"
                                                            : "bg-green-500/20 border border-green-500/40 text-green-200 hover:bg-green-500/30"
                                                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                                                    >
                                                        {isCurrentlyUpdating ? (
                                                            <Loader2 className="w-3 h-3 animate-spin" />
                                                        ) : isAppActive ? (
                                                            <ToggleRight className="w-4 h-4" />
                                                        ) : (
                                                            <ToggleLeft className="w-4 h-4" />
                                                        )}
                                                        <span className="font-medium">
                                                            {isCurrentlyUpdating
                                                                ? "Updating..."
                                                                : isAppActive
                                                                    ? "Deactivate"
                                                                    : "Activate"}
                                                        </span>
                                                    </button>
                                                </div>
                                                {/* --- End of Integrated Block --- */}

                                                <p className="text-sm text-white/60">
                                                    Created: {formatTimestamp(config.creation_time_str)}
                                                </p>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-center py-10 text-white/60">
                                    <MessageSquare className="w-14 h-14 mx-auto mb-3 opacity-50" />
                                    <p>No SMS configurations found</p>
                                </div>
                            )}
                        </div>
                    </div>


                    {/* Right Content Area - Form */}
                    <div className="lg:col-span-2">
                        {/* Queue Management Section */}
                        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-2xl">
                            <div className="flex items-center space-x-3 mb-6">
                                <div className="w-10 h-10 bg-cyan-500/20 rounded-xl flex items-center justify-center">
                                    <Activity className="w-5 h-5 text-cyan-400" />
                                </div>
                                <h3 className="text-xl font-semibold text-white">Queue Management</h3>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Current Queue Status */}
                                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                                    <div className="flex items-center space-x-3 mb-3">
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${currentQueueType !== null ? `bg-${getCurrentQueueColor()}-500/20` : 'bg-gray-500/20'
                                            }`}>
                                            {isLoadingQueue ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                queueTypes.find(q => q.value === currentQueueType)?.icon || <Server className="w-4 h-4" />
                                            )}
                                        </div>
                                        <div>
                                            <label className="text-white/60 text-xs uppercase tracking-wider">Current Queue</label>
                                            <p className={`font-medium text-sm ${currentQueueType !== null ? `text-${getCurrentQueueColor()}-300` : 'text-gray-300'
                                                }`}>
                                                {isLoadingQueue ? 'Loading...' : getCurrentQueueLabel()}
                                            </p>
                                        </div>
                                    </div>
                                    {currentQueueType !== null && (
                                        <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-xs bg-${getCurrentQueueColor()}-500/20 text-${getCurrentQueueColor()}-300`}>
                                            <div className={`w-2 h-2 rounded-full bg-${getCurrentQueueColor()}-400 animate-pulse`}></div>
                                            <span>Active</span>
                                        </div>
                                    )}
                                </div>

                                {/* Queue Type Selection */}
                                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                                    <label className="text-white/60 text-xs uppercase tracking-wider mb-3 block">Switch Queue Type</label>
                                    <select
                                        value={selectedQueueType}
                                        onChange={(e) => setSelectedQueueType(e.target.value)}
                                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all duration-300 mb-4"
                                        disabled={isUpdatingQueue}
                                    >
                                        {queueTypes.map((type) => (
                                            <option key={type.value} value={type.value} className="bg-slate-800 text-white">
                                                {type.label}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        onClick={updateQueueType}
                                        disabled={isUpdatingQueue || selectedQueueType === currentQueueType}
                                        className="w-full py-2 rounded-lg font-medium text-sm transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600"
                                    >
                                        {isUpdatingQueue ? (
                                            <div className="flex items-center justify-center space-x-2">
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                <span>Updating...</span>
                                            </div>
                                        ) : (
                                            <span>Switch Queue Type</span>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                        {/* sms config form */}
                        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-xl">
                            <div className="flex items-center justify-between mb-8">
                                <div className="flex items-center space-x-4">
                                    <div className="w-12 h-12 bg-orange-600/20 rounded-xl flex items-center justify-center">
                                        <Plus className="w-6 h-6 text-orange-400" />
                                    </div>
                                    <h3 className="text-2xl font-semibold">
                                        {isEditing ? 'Edit SMS Configuration' : 'Create SMS Configuration'}
                                    </h3>
                                </div>
                                {isEditing && (
                                    <button
                                        onClick={resetForm}
                                        className="flex items-center space-x-2 px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/10 rounded-xl transition-all duration-300 shadow-sm"
                                    >
                                        <X className="w-5 h-5" />
                                        <span>Cancel Edit</span>
                                    </button>
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* App Name */}
                                <div>
                                    <label className="block text-white/90 text-sm font-medium mb-2">
                                        <User className="w-4 h-4 inline mr-2" />
                                        App Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.sms_config_app_name}
                                        onChange={(e) => handleInputChange('sms_config_app_name', e.target.value)}
                                        placeholder="Enter app name..."
                                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all duration-300 shadow-sm"
                                        disabled={isSaving}
                                    />
                                </div>

                                {/* SMS URL */}
                                <div>
                                    <label className="block text-white/90 text-sm font-medium mb-2">
                                        <Globe className="w-4 h-4 inline mr-2" />
                                        SMS URL *
                                    </label>
                                    <input
                                        type="url"
                                        value={formData.sms_url}
                                        onChange={(e) => handleInputChange('sms_url', e.target.value)}
                                        placeholder="https://api.sms-provider.com/send"
                                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all duration-300 shadow-sm"
                                        disabled={isSaving}
                                    />
                                </div>

                                {/* Username */}
                                <div>
                                    <label className="block text-white/90 text-sm font-medium mb-2">
                                        <User className="w-4 h-4 inline mr-2" />
                                        Username *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.sms_username}
                                        onChange={(e) => handleInputChange('sms_username', e.target.value)}
                                        placeholder="Enter username..."
                                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all duration-300 shadow-sm"
                                        disabled={isSaving}
                                    />
                                </div>

                                {/* Password */}
                                <div>
                                    <label className="block text-white/90 text-sm font-medium mb-2">
                                        <Lock className="w-4 h-4 inline mr-2" />
                                        Password *
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            value={formData.sms_password}
                                            onChange={(e) => handleInputChange('sms_password', e.target.value)}
                                            placeholder="Enter password..."
                                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all duration-300 shadow-sm"
                                            disabled={isSaving}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/60 hover:text-white transition-colors"
                                        >
                                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        </button>
                                    </div>
                                </div>

                                {/* SMS Sender */}
                                <div>
                                    <label className="block text-white/90 text-sm font-medium mb-2">
                                        <Send className="w-4 h-4 inline mr-2" />
                                        SMS Sender *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.sms_sender}
                                        onChange={(e) => handleInputChange('sms_sender', e.target.value)}
                                        placeholder="Enter sender name/ID..."
                                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all duration-300 shadow-sm"
                                        disabled={isSaving}
                                    />
                                </div>

                                {/* Vendor Name */}
                                <div>
                                    <label className="block text-white/90 text-sm font-medium mb-2">
                                        <Settings className="w-4 h-4 inline mr-2" />
                                        Vendor Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.sms_vendor_name}
                                        onChange={(e) => handleInputChange('sms_vendor_name', e.target.value)}
                                        placeholder="Enter vendor name..."
                                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all duration-300 shadow-sm"
                                        disabled={isSaving}
                                    />
                                </div>

                                {/* PE ID */}
                                <div>
                                    <label className="block text-white/90 text-sm font-medium mb-2">
                                        <Settings className="w-4 h-4 inline mr-2" />
                                        Pe Id *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.pe_id}
                                        onChange={(e) => handleInputChange('pe_id', e.target.value)}
                                        placeholder="Enter Pe Id..."
                                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all duration-300 shadow-sm"
                                        disabled={isSaving}
                                    />
                                </div>

                                {/* DLT CHAIN */}
                                <div>
                                    <label className="block text-white/90 text-sm font-medium mb-2">
                                        <Settings className="w-4 h-4 inline mr-2" />
                                        Dlt Chain *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.dlt_chain}
                                        onChange={(e) => handleInputChange('dlt_chain', e.target.value)}
                                        placeholder="Enter Dlt Chain..."
                                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all duration-300 shadow-sm"
                                        disabled={isSaving}
                                    />
                                </div>

                                {/* Token */}
                                <div>
                                    <label className="block text-white/90 text-sm font-medium mb-2">
                                        <Settings className="w-4 h-4 inline mr-2" />
                                        Token *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.token}
                                        onChange={(e) => handleInputChange('token', e.target.value)}
                                        placeholder="Enter token..."
                                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all duration-300 shadow-sm"
                                        disabled={isSaving}
                                    />
                                </div>
                                {/* Manager Email Id */}
                                <div>
                                    <label className="block text-white/90 text-sm font-medium mb-2">
                                        <Settings className="w-4 h-4 inline mr-2" />
                                        Manager Notification Email *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.notification_email}
                                        onChange={(e) => handleInputChange('notification_email', e.target.value)}
                                        placeholder="Enter Manager Notification Email..."
                                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all duration-300 shadow-sm"
                                        disabled={isSaving}
                                    />
                                </div>
                            </div>

                            {/* Config Type */}
                            <div className="mt-6">
                                <label className="block text-white/90 text-sm font-medium mb-2">
                                    <FileText className="w-4 h-4 inline mr-2" />
                                    Configuration Type *
                                </label>
                                <select
                                    value={formData.sms_config_type}
                                    onChange={(e) => handleInputChange('sms_config_type', e.target.value)}
                                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all duration-300 shadow-sm"
                                    disabled={isSaving}
                                >
                                    <option value="">Select configuration type...</option>
                                    <option value="BULK">BULK</option>
                                    <option value="SINGLE">SINGLE</option>
                                </select>
                            </div>

                            {/* Save Button */}
                            <button
                                onClick={handleSaveConfig}
                                disabled={isSaving}
                                className="w-full mt-8 py-4 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl disabled:cursor-not-allowed transform hover:scale-[1.02] transition-all duration-300 relative overflow-hidden bg-gradient-to-r from-orange-600 via-red-600 to-pink-600 hover:from-orange-700 hover:via-red-700 hover:to-pink-700 disabled:opacity-50 group"
                            >
                                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                                {isSaving ? (
                                    <div className="flex items-center justify-center space-x-3 text-white relative z-10">
                                        <Loader2 className="w-6 h-6 animate-spin" />
                                        <span>{isEditing ? 'Updating...' : 'Saving...'}</span>
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center space-x-3 text-white relative z-10">
                                        <Save className="w-6 h-6" />
                                        <span>{isEditing ? 'Update Configuration' : 'Save Configuration'}</span>
                                    </div>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Details Modal */}
                {selectedConfig && (
                    <Modal
                        isOpen={isDetailsModalOpen}
                        onClose={() => setIsDetailsModalOpen(false)}
                        title={`${selectedConfig.sms_config_app_name} Details`}
                    >
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Left Column */}
                                <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                                    <label className="text-white/60 text-xs uppercase tracking-wider block mb-2">App Name</label>
                                    <p className="font-medium text-white">{selectedConfig.sms_config_app_name}</p>
                                </div>

                                {/* Right Column */}
                                <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                                    <label className="text-white/60 text-xs uppercase tracking-wider block mb-2">SMS Sender</label>
                                    <p className="font-medium text-white">{selectedConfig.sms_sender}</p>
                                </div>

                                {/* SMS URL */}
                                <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                                    <label className="text-white/60 text-xs uppercase tracking-wider block mb-2">SMS URL</label>
                                    <p className="font-medium text-white text-sm break-all">{selectedConfig.sms_url}</p>
                                </div>

                                {/* Vendor Name */}
                                <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                                    <label className="text-white/60 text-xs uppercase tracking-wider block mb-2">Vendor Name</label>
                                    <p className="font-medium text-white">{selectedConfig.sms_vendor_name}</p>
                                </div>

                                {/* Username */}
                                <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                                    <label className="text-white/60 text-xs uppercase tracking-wider block mb-2">Username</label>
                                    <p className="font-medium text-white">{selectedConfig.sms_username}</p>
                                </div>

                                {/* Configuration Type */}
                                <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                                    <label className="text-white/60 text-xs uppercase tracking-wider block mb-2">Configuration Type</label>
                                    <p className="font-medium text-white">{selectedConfig.sms_config_type}</p>
                                </div>

                                {/* Creation Date */}
                                <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                                    <label className="text-white/60 text-xs uppercase tracking-wider block mb-2">Creation Date</label>
                                    <p className="font-medium text-white">{formatTimestamp(selectedConfig.creation_time_str)}</p>
                                </div>

                                {/* DLT Chain */}
                                <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                                    <label className="text-white/60 text-xs uppercase tracking-wider block mb-2">DLT Chain</label>
                                    <p className="font-medium text-white text-sm break-all">{selectedConfig.dlt_chain}</p>
                                </div>

                                {/* Token */}
                                <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                                    <label className="text-white/60 text-xs uppercase tracking-wider block mb-2">Token</label>
                                    <p className="font-medium text-white text-sm break-all">{selectedConfig.token}</p>
                                </div>

                                {/* Status */}
                                <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                                    <label className="text-white/60 text-xs uppercase tracking-wider block mb-2">Status</label>
                                    <div className="flex items-center space-x-2">
                                        <div className={`w-2 h-2 rounded-full ${selectedConfig.STATUS === 'ACTIVE' ? 'bg-green-400' : 'bg-red-400'}`}></div>
                                        <span className={`font-medium ${selectedConfig.STATUS === 'ACTIVE' ? 'text-green-300' : 'text-red-300'}`}>
                                            {selectedConfig.STATUS}
                                        </span>
                                    </div>
                                </div>

                                {/* peId */}
                                <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                                    <label className="text-white/60 text-xs uppercase tracking-wider block mb-2">peId</label>
                                    <p className="font-medium text-white">{selectedConfig.pe_id}</p>
                                </div>
                                {/* Manager Notification Email */}
                                <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                                    <label className="text-white/60 text-xs uppercase tracking-wider block mb-2">Manager Notification Email</label>
                                    <p className="font-medium text-white">{selectedConfig.notification_email}</p>
                                </div>
                            </div>

                            {/* Password Section - Full Width */}
                            <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <label className="text-white/60 text-xs uppercase tracking-wider">Password</label>
                                    <button
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="flex items-center space-x-1 px-2 py-1 bg-white/10 hover:bg-white/20 border border-white/10 rounded text-white/80 transition-all duration-300 text-sm"
                                    >
                                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        <span>{showPassword ? 'Hide' : 'Show'}</span>
                                    </button>
                                </div>
                                <div className="bg-slate-900/50 border border-white/10 rounded p-3">
                                    <p className="font-mono text-sm text-white/80 break-all">
                                        {showPassword ? selectedConfig.sms_password : '••••••••••••••••'}
                                    </p>
                                </div>
                            </div>

                            {/* Last Updated - If exists */}
                            {selectedConfig.UPDATE_TIME && (
                                <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                                    <label className="text-white/60 text-xs uppercase tracking-wider block mb-2">Last Updated</label>
                                    <p className="font-medium text-white">{formatTimestamp(selectedConfig.UPDATE_TIME)}</p>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex items-center justify-start gap-3 pt-2">
                                <button
                                    onClick={() => {
                                        loadConfigForEdit(selectedConfig);
                                        setIsDetailsModalOpen(false);
                                    }}
                                    disabled={selectedConfig.STATUS !== 'ACTIVE'}
                                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-300 ${selectedConfig.STATUS === 'ACTIVE'
                                        ? 'bg-blue-600/80 hover:bg-blue-600 text-white'
                                        : 'bg-gray-600/50 text-gray-400 cursor-not-allowed'
                                        }`}
                                >
                                    <Edit3 className="w-4 h-4" />
                                    <span>Edit</span>
                                </button>
                                <button
                                    onClick={() => handleToggleClick(selectedConfig)}
                                    disabled={isUpdating}
                                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-300 ${selectedConfig.STATUS === 'ACTIVE'
                                        ? 'bg-green-600/80 hover:bg-green-600 text-white'
                                        : 'bg-red-600/80 hover:bg-red-600 text-white'
                                        } disabled:opacity-50`}
                                >
                                    {isUpdating && configToUpdate?._id === selectedConfig._id ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : selectedConfig.STATUS === 'ACTIVE' ? (
                                        <ToggleRight className="w-5 h-5" />
                                    ) : (
                                        <ToggleLeft className="w-5 h-5" />
                                    )}
                                    <span>{selectedConfig.STATUS === 'ACTIVE' ? 'Active' : 'Inactive'}</span>
                                </button>
                            </div>

                            {/* Info Message */}
                            {selectedConfig.STATUS !== 'ACTIVE' && (
                                <div className="flex items-start space-x-2 text-yellow-400 bg-yellow-400/10 border border-yellow-400/20 rounded-lg p-3">
                                    <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                    <span className="text-xs">Editing is only available when configuration is active.</span>
                                </div>
                            )}
                        </div>
                    </Modal>
                )}

                {/* License Modal */}
                <Modal isOpen={isLicenseModalOpen} onClose={() => setIsLicenseModalOpen(false)} title="License Key Required">
                    <div className="text-center">
                        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-purple-600/20 border border-purple-600/40 mb-6">
                            <KeyRound className="h-8 w-8 text-purple-300" />
                        </div>
                        <p className="text-white/70 mb-6">Please enter your license number to proceed with this action.</p>
                        <input
                            type="text"
                            value={licenseNo}
                            onChange={(e) => {
                                setLicenseNo(e.target.value);
                                if (licenseError) setLicenseError('');
                            }}
                            placeholder="Enter license number..."
                            className="w-full px-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all duration-300 shadow-sm"
                        />
                        {licenseError && <p className="text-red-400 text-sm mt-2">{licenseError}</p>}
                        <div className="mt-8 flex justify-end space-x-4">
                            <button onClick={() => setIsLicenseModalOpen(false)} className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 border border-white/10 transition-colors shadow-sm">Cancel</button>
                            <button onClick={handleLicenseSubmit} className="px-6 py-3 rounded-xl bg-purple-600 hover:bg-purple-700 font-semibold transition-colors shadow-sm">Submit</button>
                        </div>
                    </div>
                </Modal>

                {/* Confirmation Modal */}
                <Modal isOpen={isConfirmModalOpen} onClose={() => setIsConfirmModalOpen(false)} title="Confirm Action">
                    {configToUpdate && (
                        <div className="text-center">
                            <div className={`mx-auto flex items-center justify-center h-16 w-16 rounded-full border mb-6 ${configToUpdate.STATUS === 'ACTIVE' ? 'bg-red-600/20 border-red-600/40' : 'bg-green-600/20 border-green-600/40'
                                }`}>
                                <ShieldCheck className={`h-8 w-8 ${configToUpdate.STATUS === 'ACTIVE' ? 'text-red-300' : 'text-green-300'
                                    }`} />
                            </div>
                            <p className="text-white/70 mb-6">
                                Are you sure you want to <span className="font-bold">{configToUpdate.STATUS === 'ACTIVE' ? 'deactivate' : 'activate'}</span> the SMS configuration for <span className="font-bold text-purple-300">{configToUpdate.sms_config_app_name}</span>?
                            </p>
                            <div className="mt-8 flex justify-end space-x-4">
                                <button onClick={() => setIsConfirmModalOpen(false)} className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 border border-white/10 transition-colors shadow-sm">Cancel</button>
                                <button
                                    onClick={executeStatusUpdate}
                                    className={`px-6 py-3 rounded-xl font-semibold transition-colors shadow-sm ${configToUpdate.STATUS === 'ACTIVE' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
                                        }`}
                                >
                                    {isUpdating ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : `Yes, ${configToUpdate.STATUS === 'ACTIVE' ? 'Deactivate' : 'Activate'}`}
                                </button>
                            </div>
                        </div>
                    )}
                </Modal>

                <style jsx>{`
                    .custom-scrollbar {
                        scrollbar-width: thin;
                        scrollbar-color: rgba(100, 116, 139, 0.4) rgba(51, 65, 85, 0.4);
                    }
                    .custom-scrollbar::-webkit-scrollbar {
                        width: 8px;
                    }
                    .custom-scrollbar::-webkit-scrollbar-track {
                        background-color: rgba(51, 65, 85, 0.4);
                        border-radius: 0.5rem;
                    }
                    .custom-scrollbar::-webkit-scrollbar-thumb {
                        background-color: rgba(100, 116, 139, 0.4);
                        border-radius: 0.5rem;
                    }
                    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                        background-color: rgba(100, 116, 139, 0.6);
                    }
                `}</style>
            </div>
        </div>
    );
}